import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useTranslation } from 'react-i18next';

import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';

import { Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { ControlledToggle } from 'src/components/ui/Toggle/Toggle';
import styles from './Quality.module.scss';
import QualityCheckTable from './QualityCheckTable/QualityCheckTable';

import { ProductFormValues } from '../ProductFormTypes';
import { createEmptyCheck } from './QualityHelpers';

export interface QualityProps {
  editModeActive: boolean;
  productId: number;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
}

const Quality: React.FC<QualityProps> = ({ control, editModeActive, errors, productId, setValue }) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const checks = useWatch({ control, name: 'qualityChecks' });
  const qualityCheckEnabled = useWatch({ control, name: 'qualityCheckEnabled' });

  const handleCreateQualityCheck = () => {
    const newChecks = structuredClone(checks ?? []);
    newChecks.push(createEmptyCheck({ productId }));
    setValue('qualityChecks', newChecks);
  };

  const initQualityChecks = qualityCheckEnabled && editModeActive && (checks ?? []).length === 0;
  useEffect(() => {
    if (initQualityChecks) {
      setValue('qualityChecks', [createEmptyCheck({ productId })]);
    }
  }, [initQualityChecks]);

  return (
    <div className={classNames(styles.root)}>
      <div className="flex justify-between items-center mb-16 mt-24">
        <h4 className="typo-text-16--bold text-full">{t('quality.quality')}</h4>
        <div className="flex items-center gap-12">
          <ControlledToggle
            control={control}
            name="qualityCheckEnabled"
            label={qualityCheckEnabled ? t('quality.quality_checks_enabled') : t('quality.quality_checks_disabled')}
            disabled={!editModeActive}
            horizontalLabel
          />
        </div>
      </div>
      {qualityCheckEnabled && (
        <>
          <QualityCheckTable
            control={control}
            errors={errors}
            setValue={setValue}
            editModeActive={editModeActive}
            expandedIndex={expandedIndex}
            onExpandToggleClick={idx => {
              if (expandedIndex === idx) {
                setExpandedIndex(null);
              } else {
                setExpandedIndex(idx);
              }
            }}
            setExpandedIndex={setExpandedIndex}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleCreateQualityCheck}
              tone={ButtonToneProps.SECONDARY}
              title={t('quality.create')}
              disabled={!editModeActive}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Quality;
